import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Form, Alert, Row, Col } from 'react-bootstrap';
import { useUsers } from '@hooks';
import { useAuth } from '@context';
import { canManageCourse } from '@utils/permissions';

/**
 * Course Enrollment Manager Component
 * Manages user enrollments for a specific course
 */
export function CourseEnrollmentManager({ courseId, courseName, onBack }) {
  const { users, enrollUser, unenrollUser, loading: usersLoading } = useUsers();
  const { user } = useAuth();
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  /**
   * Update enrolled and available users lists
   */
  useEffect(() => {
    if (users.length > 0 && courseId) {
      const students = users.filter(u => u.role === 'student');
      const enrolled = students.filter(u => (u.enrolledCourses || []).includes(courseId));
      const available = students.filter(u => !(u.enrolledCourses || []).includes(courseId));
      setEnrolledUsers(enrolled);
      setAvailableUsers(available);
    }
  }, [users, courseId]);

  if (!canManageCourse(user, courseId)) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger" className="mb-0">Access denied: You are not assigned to this course.</Alert>
        </Card.Body>
      </Card>
    );
  }

  /**
   * Handle enrolling a user
   */
  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setError('Please select a user to enroll');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await enrollUser(selectedUser, courseId);
      setSuccess('User enrolled successfully');
      setSelectedUser('');
    } catch (err) {
      setError(`Failed to enroll user: ${err.message}`);
    }
  };

  /**
   * Handle unenrolling a user
   */
  const handleUnenroll = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to unenroll ${userName} from this course?`)) {
      try {
        setError(null);
        setSuccess(null);
        await unenrollUser(userId, courseId);
        setSuccess('User unenrolled successfully');
      } catch (err) {
        setError(`Failed to unenroll user: ${err.message}`);
      }
    }
  };

  if (usersLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-3">
        <Button variant="link" onClick={onBack}>← Back to Users</Button>
      </div>

      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Course Enrollments: {courseName}</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Enroll New User Form */}
          <Card className="mb-3 bg-light">
            <Card.Body>
              <h6>Enroll New User</h6>
              <Form onSubmit={handleEnroll}>
                <Row className="align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Select User</Form.Label>
                      <Form.Select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                      >
                        <option value="">Choose a user...</option>
                        {availableUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Button 
                      type="submit" 
                      variant="primary" 
                      disabled={!selectedUser}
                      className="w-100"
                    >
                      Enroll User
                    </Button>
                  </Col>
                </Row>
              </Form>
              {availableUsers.length === 0 && (
                <small className="text-muted">All users are already enrolled in this course.</small>
              )}
            </Card.Body>
          </Card>

          {/* Enrolled Users List */}
          <h6>Enrolled Users ({enrolledUsers.length})</h6>
          {enrolledUsers.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No users enrolled in this course yet.
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enrolledUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <Badge bg="primary">
                        {user.role}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={user.isActive ? 'success' : 'secondary'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleUnenroll(user.id, user.name)}
                      >
                        Unenroll
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
